<template>
  <!-- Karta z formularzem resetu hasła -->
  <div class="card m-3 text-center">
    <!-- Nagłówek formularza -->
    <h4 class="card-header">{{ t('reset-password') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <!-- Formularz z walidacją -->
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        v-slot="{ errors, isSubmitting }"
      >
        <!-- Pole e-mail -->
        <div class="form-group row mb-1">
          <label class="col-sm-2 col-form-label">{{ t('email') }}</label>
          <div class="col-sm-10">
            <Field
              name="email"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
            />
            <!-- Komunikat o błędzie -->
            <div class="invalid-feedback">{{ errors.email }}</div>
          </div>
        </div>

        <!-- Instrukcja i przycisk wysyłania -->
        <div class="form-group row d-flex flex-column align-items-center">
          <div class="col-auto">
            {{ t('type-your-email-send-reset-token') }}
          </div>
          <div class="col-auto">
            <button
              class="btn btn-outline-secondary mt-3"
              :disabled="isSubmitting"
            >
              <span
                v-show="isSubmitting"
                class="spinner-border spinner-border-sm mr-1"
              ></span>
              {{ t('send-email') }}
            </button>
          </div>
          <div class="form-status col-12 mt-3">
            <div
              v-if="statusMessage && !statusDismissed"
              class="alert alert-dismissible fade show"
              :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
              role="alert"
            >
              {{ statusMessage }}
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="dismissStatus"
              ></button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
// Importy niezbędnych narzędzi i bibliotek
import { useAuthStore } from "@/stores/auth.store";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useI18n } from 'vue-i18n';
import { ref } from "vue";

// Hook do tłumaczeń
const { t } = useI18n();
const statusMessage = ref("");
const statusType = ref("error");
const statusDismissed = ref(false);

const showStatus = (message, type = "error") => {
  statusMessage.value = message;
  statusType.value = type;
  statusDismissed.value = false;
};
const dismissStatus = () => {
  statusDismissed.value = true;
};

// Definicja schematu walidacji dla pola e-mail
const schema = Yup.object({
  email: Yup.string()
    .email(t('incorrect-email-address'))        // komunikat przy niepoprawnym mailu
    .required(t('user-email-required')),        // komunikat przy pustym polu
});

// Funkcja obsługująca submit formularza (reset hasła)
async function onSubmit(values) {
  const { email } = values;
  const authStore = useAuthStore();
  statusMessage.value = "";
  statusDismissed.value = false;
  try {
    await authStore.resetPassword(email);
    showStatus("Jeśli podany adres istnieje w systemie, wysłaliśmy instrukcję resetu hasła.", "success");
  } catch (error) {
    showStatus(error?.message || "Nie udało się wysłać wiadomości resetującej hasło.", "error");
  }
}
</script>

<style scoped>
.card-body {
  background: #f5f4f3;
}
.card-body form {
  width: 400px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: 12px;
}
button:not(.btn-close) {
  border: 0;
  color: #231f20;
  font-size: 15px;
  font-weight: bold;
}
a {
  color: #231f20;
  font-size: 12px;
  text-decoration: none;
}
input {
  border: 0;
  border-left: 1px solid #c8c7c7;
  border-top: 1px solid #c8c7c7;
  padding: 10px;
}
input:focus {
  border-color: #c8c7c7;
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.form-status .alert {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}
.form-status .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
</style>
